import { LightningElement, track, api } from 'lwc';


// ================================= Tutorial ================================= \\
// Required import: import { showModal } from '../../../utils/popoverUtils.js';

/*  Case 1: Simplest Setup (plain text body with ok/cancel):
    **myTitle = 'Delete';
    **myBodyMessage = 'Are you sure you want to delete';

    // Call the utility function 
    let result = await showModal({title: myTitle, bodyMessage: myBodyMessage})

    if (result === 'cancel') {
        return;
    } else {
        // Do something
    }
*/

/* Case 2: Fields Setup (without dependent fields):
    myColumns = 2 // Set amount of  columns in each row in the modal
    myBodyMesssage = 'Some extra info can go in here'
    **myTitle = 'Delete';
    **myFields = [
        {
            **fieldName: 'delete', 
            **label: `Select rows to Delete`, 
            value: 'all', 
            **dtype: 'radio', 
            isRequired: true
            *options: [{label: 'All checked rows', value: 'all'}, {label: 'Only selected row', value: 'selected'}],
        }, 
        {
            **fieldName: 'ema', 
            **label: 'Ema', 
            value: 21,
            ?min: 1,                <-- can add  min/max for number inputs
            **dtype: 'number'
        }
    ];

    let result = await showModal({title: myTitle, fields: myFields, bodyMessage: myBodyMessage, columns: myColumns})

    if (result === 'cancel' || !result) {return;}
    if (typeof result === 'object') {
        for (const key of Object.keys(result)) {
            console.log('this is the fieldName key from myFields: ', key, ' this is the value <result> returned: ', result[key]);
        }
    }
*/

// ============================================================================ \\

export default class sldsModalPop extends LightningElement {
    static renderMode = 'light';
    @track fieldForm = {};         // Structure the data as a record object
    @track requiredFields = [];   // Store the required fields
    @track isRendered = false;
    @track helpDetails = {isHelp: false, isArray: false}; // Boolean for if 'helpSection' is an Array  ---> else String
    

    // ================ Public Properties ================ \\
    @api size; // Set the size of the modal
    @api columns;
    @api 
    set fields(value) {   
        // Get the required fields
        if (value) {
            this.requiredFields = value.filter(field => field.isRequired).map(field => field.fieldName);
        }
        // Store the fields
        this._fields = JSON.parse(JSON.stringify(value)).map(field => {
            if (field.value) {
                this.fieldForm[field.fieldName] = field.value;
            }
            return {...field, [ `is${field.dtype || 'text'}`]: true };
        });
    }
    get fields() {
        return this._fields;
    }
    
    @api bodyText; // Plain text for the Modal Body
    @api title; // Header title of the modal
    @api 
    set helpSection(value) {
        this.helpDetails = {
            isArray: value && Array.isArray(value),
            isHelp: value ? true : false
        }
        this._helpSection = {listText: value};
    }
    get helpSection() {
        return this._helpSection;
    }
    // =================================================== \\  


    // Define <-size-> of Modal if not null or medium(default)
    renderedCallback() {
        // Adjust the modal's size if specified
        if (this.size) {
            // Make sure the size parameter is an available size
            const sizeLower = this.size.toLowerCase(); 
            if (!['small', 'medium', 'large'].some(sz => sz === sizeLower)) { return; }
            const modal = this.querySelector('.slds-modal');
            if (modal) {
                modal.classList.add(`slds-modal_${sizeLower}`);
            }
        } 
        console.log('Here are the fields in renderedCallback: ', JSON.stringify(this._fields));

        // Adjust Columns per row if specified (slds-size_1-of<columns>)
        if (this.columns && this.columns !== 1) {
            const columns = parseInt(this.columns);                  // Convert the column amount to an int
            const gridCols = this.querySelectorAll('.field-row');   // Get all rows in the slds-grid
            if (gridCols) {
                const newClass = `slds-size_1-of-${columns}`;     // Define the new class (slds-size_1-of<columns>)
                gridCols.forEach(grid => 
                    grid.classList.replace('slds-size_1-of-1', newClass)
                );                                             // Replace slds-size_1-of-1
            }
        }

        // Add focus to the first element When the modal renders
        const firstElement = this.querySelector('[data-is-first="true"]');
        if (firstElement && !this.isRendered) {
            firstElement.focusMe();
            this.isRendered = true;
        } else {
            console.log('sldsModalPop NOT got firstElement: ');
        }
    }

    // Return True if field is required
    checkIfRequired(fieldName) {
        return new Set(this.requiredFields).has(fieldName);
    }
    // Handle message sent from the child component --->  update the <fieldForm> object
    handleNewInput(event) {
        // Get the fieldName from the dataset-id
        const fieldName = event.currentTarget.dataset.id;

        // Get the message from the child
        const details = event.detail;
        console.log('details: ', JSON.stringify(details)); 

        // Flag required
        const isRequired = this.checkIfRequired(fieldName);

        // Get the value sent by the childs
        let val = details ? details[Object.keys(details)[0]] : null;
        console.log('value: ', val); 

        // Flag if the user input was an error
        const isError = isRequired && !val ? true : false;

        // Update the fieldForm data to reflect the current value of the child
        this.fieldForm[fieldName] = val;

        if (isError) {
            this.triggerError(fieldName);
        }
    }
     
    // Get the amount of columns in each row  of the modal
    get colGridSizeClass() {
        return this.columns 
            ? `field-row slds-col slds-size_1-of-${this.columns} slds-p-around_medium`
            : 'field-row slds-col slds-size_1-of-1 slds-p-around_medium';
    }


    // Close the modal
    handleCancel() {
        this.dispatchEvent(new CustomEvent('cancel', { bubbles: true }));
    }
    // Verify that all the required fields have been filled
    handleOkay() {
        let isSave = true;
        if (this._fields) {
            // ------------ Request the value from the child components ------------ \\
            for (const fieldObj of this._fields) {
                if (fieldObj?.fieldName) {
                    const fieldVal = this.querySelector(`[data-id="${fieldObj.fieldName}"]`).sendValues();
                    if (fieldVal) {
                        this.fieldForm[fieldObj.fieldName] = fieldVal;
                    }
                }
            }
            // ---------------------------------------------------------------------- \\ 
        }

        
        // Handle <_fields> length greater than 0 modal message
        if (this._fields && this._fields.length > 0) {
            // Ensure required fields are filled if fields
            if (this.requiredFields && this.requiredFields.length > 0) {
                for (const required of this.requiredFields) {

                    if (!this.fieldForm[required]) {
                        console.log('required is false');
                        this.querySelector(`[data-id="${required}"]`).errorMe();
                        isSave = false;
                    }
                }
            }
            // Send a custom event with the fieldForm data
            if (isSave) {
                this.dispatchEvent(
                    new CustomEvent('confirm', {
                        detail: this.fieldForm,
                        bubbles: true
                    })
                );
            }
            
        } else {
            // Handle simple modal (only body)
            this.dispatchEvent(
                new CustomEvent('confirm', {
                    detail: 'confirm',
                    bubbles: true
                })
            );
        }
    }
    
}